import {useEffect} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {hideToast, lockTurnInput, releaseTurnInput} from '../../redux/actions';
import {
  selectIsAwaitingServerTurnTimestamp,
  selectReleasedTurnInputKey,
  selectServerTimeDelta,
  selectToastState,
} from '../../redux/reducers/privateStateReducer/selectors';
import {
  TURN_INPUT_LOCK_MAX_MS,
  selectIsTurnInputLocked,
  selectTurnInputLockDeadlineMs,
  selectTurnInputLockKey,
} from '../../utils/gameState/turn/turnInputLock';
import {TEXT_TURN_TIME_IS_UP} from '../../constants/text/turn';
import {REGISTRY_SINGLETON} from '../../scenes/Registry';

// То, что игрок начал ДО нуля, должно успеть уйти на сервер — отсрочка
// серверного автозавершения для этого и существует. Перемещение отряда пишется
// в стор только в конце полета, а первый клик возможной пары двойного клика
// ждет своего окна: и то и другое доводится до конца, пока ввод еще открыт
const finishStartedPlayerInput = () => {
  REGISTRY_SINGLETON.getGameScene()?.flushDeferredFirstClickEffect?.();
  REGISTRY_SINGLETON.getHexGridRenderer()?.skipUnitMovementAnimation?.();
};

/**
 * Блокировка ввода после нуля таймера хода (см. utils/gameState/turn/turnInputLock).
 *
 * В момент, когда у игрока вышло время его хода (или его части одновременного
 * этапа), ставит признак блокировки для этого хода. Снимать признак при смене
 * хода не нужно — он действует, только пока ход тот же. Если закрытие от
 * сервера так и не пришло, признак снимает предохранитель, и для этого хода
 * блокировка больше не ставится.
 *
 * Блокировка не ставится, пока клиент ждет от сервера метку начала нового
 * отрезка (игрок сам завершил этап: этап в сторе уже новый, а метка — от
 * прошлого, и «время вышло» по ней было бы ложным).
 */
export const useTurnInputLock = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const turnKey = useSelector(selectTurnInputLockKey);
  const deadlineMs = useSelector(selectTurnInputLockDeadlineMs);
  const serverTimeDelta = useSelector(selectServerTimeDelta);
  const isAwaitingServerTimestamp = useSelector(selectIsAwaitingServerTurnTimestamp);
  const releasedTurnKey = useSelector(selectReleasedTurnInputKey);
  const isLocked = useSelector(selectIsTurnInputLocked);

  // Постановка блокировки: таймер до клиентского нуля своего времени
  useEffect(() => {
    if (!turnKey || deadlineMs === null || isAwaitingServerTimestamp || releasedTurnKey === turnKey) {
      return undefined;
    }

    // Срок — в серверном времени; часы клиента сдвинуты от него на serverTimeDelta
    const lockAtMs = deadlineMs - serverTimeDelta;
    // Срок вышел давно, а ход не закрыт: часам верить нельзя, блокировать нечего
    if (Date.now() >= lockAtMs + TURN_INPUT_LOCK_MAX_MS) {
      return undefined;
    }

    const lockTimeoutId = setTimeout(() => {
      // Ход мог смениться, пока таймер ждал: эффект перезапустится только
      // после рендера
      if (selectTurnInputLockKey(store.getState()) !== turnKey) {
        return;
      }
      finishStartedPlayerInput();
      // …и от только что доведенного до конца действия тоже
      if (selectTurnInputLockKey(store.getState()) !== turnKey) {
        return;
      }
      dispatch(lockTurnInput(turnKey));
    }, Math.max(0, lockAtMs - Date.now()));

    return () => clearTimeout(lockTimeoutId);
  }, [dispatch, store, turnKey, deadlineMs, serverTimeDelta, isAwaitingServerTimestamp, releasedTurnKey]);

  // Предохранитель: блокировка держится не дольше TURN_INPUT_LOCK_MAX_MS с
  // момента постановки. Отдельным эффектом, от самой блокировки, а не от таймера
  // постановки: тот перезапускается (например, уточнилась оценка серверного
  // времени), а взведенный предохранитель при этом пропадать не должен
  useEffect(() => {
    if (!isLocked) {
      return undefined;
    }
    const releaseTimeoutId = setTimeout(() => dispatch(releaseTurnInput(turnKey)), TURN_INPUT_LOCK_MAX_MS);
    return () => clearTimeout(releaseTimeoutId);
  }, [dispatch, isLocked, turnKey]);

  // Блокировка перестала действовать (сервер закрыл ход или этап, сработал
  // предохранитель) — подсказка «Время хода вышло» больше не к месту: следующий
  // этап может быть снова своим, с открытым вводом и полным таймером. Эффект
  // срабатывает только на смену самой блокировки, чужие подсказки не трогает
  useEffect(() => {
    if (isLocked) {
      return;
    }
    const toast = selectToastState(store.getState());
    if (toast.show && toast.message === TEXT_TURN_TIME_IS_UP) {
      dispatch(hideToast());
    }
  }, [dispatch, store, isLocked]);
};
